import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration.js';
// eslint-disable-next-line no-restricted-imports -- TODO: Fix this the next time the file is edited.
import lodash from 'lodash';
dayjs.extend(duration);
const MODULE_ID = 'restoreTime';
const MONITOR_EVENTS = ['after-tick-multiple-wallet-accounts', 'after-restore'];
export class RestoreTime {
    #logger;
    #txLogMonitors;
    #restoreAtom;
    #analytics;
    #createForegroundTimer;
    #data = {
        assets: Object.create(null),
    };
    constructor({ logger, txLogMonitors, restoreAtom, analytics, createForegroundTimer, }) {
        this.#logger = logger;
        this.#txLogMonitors = txLogMonitors;
        this.#restoreAtom = restoreAtom;
        this.#analytics = analytics;
        this.#createForegroundTimer = createForegroundTimer;
    }
    start() {
        this.#txLogMonitors.on('before-start', this.#handleBeforeStart);
        MONITOR_EVENTS.forEach((monitorEvent) => {
            this.#txLogMonitors.on(monitorEvent, this.#handleAfterRestore);
        });
    }
    restoreCompleted = async ({ backupType }) => {
        this.stop();
        const item = this.#data.total;
        if (!item) {
            this.#logger.error("missing item 'total'");
            return;
        }
        item.end = new Date();
        item.duration = item.end.valueOf() - item.start.valueOf();
        const durationDisplay = this.#formatDuration(item.duration);
        this.#logger.log(`completed ${durationDisplay}`);
        const restoreTime = this.#toSeconds(item.duration);
        item.timer?.stop();
        const foregroundProps = item.timer
            ? {
                restoreForegroundMs: item.timer.getForegroundMs(),
                restoreBackgroundMs: item.timer.getBackgroundMs(),
            }
            : {};
        const orderedAssets = this.#getOrderedAssets();
        const slowestAssetsRestoreTimesProps = Object.fromEntries(orderedAssets
            .filter(([name, { duration }]) => name && duration)
            .slice(0, 3)
            .flatMap(([name, { duration }], i) => [
            [`restoredAsset${i + 1}Name`, name],
            [`restoredAsset${i + 1}Time`, duration],
        ]));
        this.#analytics.track({
            event: 'RestoreWalletSeedInputComplete',
            properties: {
                restoreTime,
                ...foregroundProps,
                backupType,
                restoredAssetCount: orderedAssets.length,
                ...slowestAssetsRestoreTimesProps,
            },
        });
    };
    stop = () => {
        this.#txLogMonitors.off('before-start', this.#handleBeforeStart);
        MONITOR_EVENTS.forEach((monitorEvent) => {
            this.#txLogMonitors.off(monitorEvent, this.#handleAfterRestore);
        });
    };
    formatData = () => {
        const ordered = this.#getOrderedAssets();
        const assets = ordered.map(([assetName, v]) => [assetName, this.#formatDuration(v.duration)]);
        return table(lodash.compact([
            this.#data.total && ['total', this.#formatDuration(this.#data.total.duration)],
            ...assets,
        ]));
    };
    #handleBeforeStart = async ({ assetName }) => {
        const isRestoring = await this.#restoreAtom.get();
        if (!isRestoring) {
            return;
        }
        if (!this.#data.total) {
            this.#data.total = {
                start: new Date(),
                timer: this.#createForegroundTimer?.(),
            };
        }
        this.#data.assets[assetName] = {
            start: new Date(),
        };
    };
    #handleAfterRestore = async ({ assetName }) => {
        const isRestoring = await this.#restoreAtom.get();
        if (!isRestoring) {
            return;
        }
        const assets = this.#data.assets;
        const item = assets[assetName];
        if (!item) {
            this.#logger.error(`missing item for '${assetName}'`);
            return;
        }
        if (item.end) {
            return;
        }
        item.end = new Date();
        item.duration = item.end.valueOf() - item.start.valueOf();
        const totalCount = Object.keys(assets).length;
        const completedCount = Object.keys(lodash.pickBy(assets, (v) => v.end)).length; // eslint-disable-line @exodus/basic-utils/prefer-basic-utils
        const remainingAssetNames = Object.keys(lodash.pickBy(assets, (v) => !v.end)); // eslint-disable-line @exodus/basic-utils/prefer-basic-utils
        const durationDisplay = this.#formatDuration(item.duration);
        const remainingDisplay = remainingAssetNames.length <= 3 && remainingAssetNames.length > 0
            ? `remaining: ${remainingAssetNames.join(', ')}`
            : '';
        this.#logger.log(`${completedCount}/${totalCount} ${assetName} ${durationDisplay} ${remainingDisplay}`);
    };
    #formatDuration = (duration) => {
        return duration === undefined ? 'restoring' : dayjs.duration(duration).format('mm:ss');
    };
    #getOrderedAssets = () => 
    // eslint-disable-next-line @exodus/basic-utils/prefer-basic-utils
    lodash.orderBy(Object.entries(this.#data.assets), (v) => v[1].duration, 'desc');
    #toSeconds = (ms) => {
        return Math.ceil(ms) / 1000;
    };
}
function table(data) {
    const maxLength = data.reduce((max, row) => Math.max(max, row[0]?.length || 0), 0);
    return data
        .map((row) => {
        const col0 = row[0]?.padEnd(maxLength, ' ');
        const col1 = row[1];
        return `${col0} : ${col1}`;
    })
        .join('\n');
}
const createRestoreTime = (opts) => new RestoreTime(opts);
const restoreTimeDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: createRestoreTime,
    dependencies: ['logger', 'txLogMonitors', 'restoreAtom', 'analytics', 'createForegroundTimer?'],
    public: true,
};
export default restoreTimeDefinition;
